/**
 * Sign in, create an account, confirm an email, and reset a forgotten password.
 *
 * Which of these are offered is the server's decision, read once from `/auth/methods`:
 * an install with no Google credentials configured should not show a Google button that
 * 404s, and one with no mail server should say "the link is in the server log" rather
 * than sending someone to check an inbox nothing was sent to.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Spinner } from "../../components/ui/spinner";
import { useSession, type RegistrationStarted } from "./session";

interface AuthMethods {
  password: boolean;
  google: boolean;
  open_registration: boolean;
  email_delivery: boolean;
  site_url?: string;
}

/** What Google's callback can send back on `?error=`, in words a person can act on. */
const OAUTH_ERRORS: Record<string, string> = {
  cancelled: "That sign-in was cancelled.",
  bad_state: "That sign-in could not be verified. Try again from this page.",
  state_expired: "That sign-in took too long. Try again.",
  email_unverified: "That Google account's email is not confirmed, so it cannot be used here.",
  wrong_domain: "That Google account is not allowed to sign in here.",
  no_account: "There is no account for that Google address, and sign-ups are closed.",
  email_taken: "An account already exists with that email. Sign in with your password instead.",
  account_disabled: "That account has been disabled.",
  google_unreachable: "Could not reach Google. Try again in a moment.",
  google_rejected: "Google would not complete that sign-in.",
};

function useAuthMethods(): AuthMethods {
  const [methods, setMethods] = useState<AuthMethods>({
    password: true,
    google: false,
    open_registration: true,
    email_delivery: false,
  });
  useEffect(() => {
    let cancelled = false;
    api
      .get<AuthMethods>("/auth/methods")
      .then((value) => !cancelled && setMethods(value))
      // Password sign-in is always available; the defaults above are the safe answer.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return methods;
}

/**
 * Where to go after signing in, if it is somewhere inside this app.
 *
 * Mirrors `oauth.safe_next_path` on the server: `//evil.example`, `https://…` and
 * `/\evil.example` (a backslash is a slash to a browser) are all somewhere else, and
 * following them would make this sign-in page somebody's phishing redirect.
 */
export function safeNext(next: string | null | undefined): string {
  // Tabs and newlines are dropped by the URL parser, so `/\t/evil.example` is `//evil…`.
  const hidden = /[\u0000-\u001f\u007f\s]/;
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.includes("\\") || hidden.test(next)) {
    return "/";
  }
  return next;
}

export function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center px-5 py-12">
      <main className="w-full max-w-sm animate-fade-in">
        <h1 className="display mb-1 text-lg">The Sheet</h1>
        <p className="mb-8 text-sm text-muted">{title}</p>
        {children}
      </main>
    </div>
  );
}

function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p
      role="alert"
      className="animate-fade-in rounded-lg border border-owing/35 bg-owing/[0.08] px-3 py-2 text-sm"
    >
      {error}
    </p>
  );
}

/**
 * Where Google sign-in has to happen, when it is not here; null when it is here.
 *
 * Google only ever returns people to the address the server is configured with. Opened
 * at any other — a phone on the Wi-Fi at the laptop's IP, while that address says
 * `localhost` — the round trip lands somewhere this device cannot reach.
 */
function googleLivesElsewhere(siteUrl: string | undefined): URL | null {
  if (!siteUrl) return null;
  try {
    const site = new URL(siteUrl);
    return site.origin === window.location.origin ? null : site;
  } catch {
    return null;
  }
}

/**
 * The Google button. A plain link, not a fetch: the whole point of the flow is a
 * top-level navigation to Google and back, and the state cookie the callback checks is
 * only set on a real navigation.
 */
function GoogleButton({ next = "/", siteUrl }: { next?: string; siteUrl?: string }) {
  const elsewhere = googleLivesElsewhere(siteUrl);
  const divider = (
    <div className="my-5 flex items-center gap-3">
      <span className="h-px flex-1 bg-rule" />
      <span className="text-xs text-muted">or</span>
      <span className="h-px flex-1 bg-rule" />
    </div>
  );
  if (elsewhere) {
    return (
      <>
        {divider}
        <p className="text-center text-xs leading-relaxed text-muted">
          Google sign-in only works at{" "}
          <a className="text-accent underline underline-offset-2" href={`${elsewhere.origin}/login`}>
            {elsewhere.host}
          </a>
          . At this address, use your email and password.
        </p>
      </>
    );
  }
  return (
    <>
      {divider}
      <a
        href={`/api/auth/google/start?next=${encodeURIComponent(next)}`}
        className="tap flex w-full items-center justify-center gap-2.5 rounded-lg border border-rule px-3 text-sm transition-colors hover:border-ink/25"
      >
        <svg viewBox="0 0 18 18" aria-hidden className="h-4 w-4">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
          />
          <path
            fill="#FBBC05"
            d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
          />
        </svg>
        Continue with Google
      </a>
    </>
  );
}

export function LoginPage() {
  const { user, login } = useSession();
  const navigate = useNavigate();
  const methods = useAuthMethods();
  const [params] = useSearchParams();
  const oauthError = params.get("error");
  const [error, setError] = useState<string | null>(
    oauthError ? (OAUTH_ERRORS[oauthError] ?? "That sign-in did not complete.") : null,
  );
  const [busy, setBusy] = useState(false);
  // Signed out on the way to somewhere (the consent page of a connected app, a group),
  // come back to it afterwards rather than to the dashboard.
  const next = safeNext(params.get("next"));

  if (user) return <Navigate to={next} replace />;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await login(String(form.get("email")), String(form.get("password")));
      navigate(next, { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Sign in to your account">
      <form
        className="flex flex-col gap-1.5"
        onSubmit={onSubmit}
        onKeyDown={(event) => {
          // Implicit submission is a browser default action, and password managers and
          // mobile keyboards both have ways of swallowing it. Signing in on Enter is the
          // whole point of a two-field form, so ask for the submit explicitly.
          if (event.key !== "Enter" || event.shiftKey || busy) return;
          if (event.target instanceof HTMLButtonElement) return;
          event.preventDefault();
          event.currentTarget.requestSubmit();
        }}
      >
        <ErrorNote error={error} />
        <label className="mt-1.5 text-sm text-muted" htmlFor="email">
          Email
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          enterKeyHint="next"
          required
          autoFocus
        />
        <label className="mt-1.5 text-sm text-muted" htmlFor="password">
          Password
        </label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          enterKeyHint="go"
          required
        />
        <Button type="submit" variant="primary" disabled={busy} className="mt-4">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <p className="mt-3 text-sm">
        <Link className="text-accent underline underline-offset-2" to="/forgot">
          Forgot your password?
        </Link>
      </p>

      {methods.google ? <GoogleButton next={next} siteUrl={methods.site_url} /> : null}

      {methods.open_registration ? (
        <p className="mt-6 text-sm text-muted">
          No account?{" "}
          <Link className="text-accent underline underline-offset-2" to="/register">
            Create one
          </Link>
        </p>
      ) : null}
    </AuthShell>
  );
}

export function RegisterPage() {
  const { user, register } = useSession();
  const navigate = useNavigate();
  const methods = useAuthMethods();
  const [params] = useSearchParams();
  // An invitation email links here with the address it was sent to. Signing up with a
  // different one produces a working account attached to no group and no explanation, so
  // the field is filled in and read-only-ish: they can still change it, but they will not
  // do so by accident.
  const invitedEmail = params.get("email") ?? "";
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<RegistrationStarted | null>(null);

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const started = await register(
        String(form.get("email")),
        String(form.get("name")),
        String(form.get("password")),
      );
      // Nothing has been created yet: the account appears when the emailed link is
      // followed. The only path straight into the app is an install with verification
      // switched off, where the server signed us in already.
      if (started.verification_required) {
        setSent(started);
      } else {
        navigate("/", { replace: true });
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not start the signup.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Confirm your email">
        <div className="text-sm">
          <p className="mb-3">
            We sent a link to <span className="font-medium">{sent.email}</span>. Open it and
            your account is ready — it is not created until you do.
          </p>
          {!sent.email_delivery ? (
            <p className="mb-3 rounded-lg border border-rule bg-raised px-3 py-2 text-xs text-muted">
              This install has no mail server configured, so the link was written to the
              server log instead of being sent. Run <code>make logs</code> to find it.
            </p>
          ) : (
            <p className="mb-3 text-xs text-muted">
              The link works for 48 hours. Check your spam folder if it has not arrived in
              a minute or two.
            </p>
          )}
          <Link className="text-accent underline underline-offset-2" to="/login">
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create an account">
      <form
        className="flex flex-col gap-1.5"
        onSubmit={onSubmit}
        onKeyDown={(event) => {
          // Creating an account is not something to do by accident: Enter moves to the
          // next field instead of submitting. Only the button creates the account.
          if (event.key !== "Enter") return;
          const target = event.target as HTMLElement;
          if (target instanceof HTMLButtonElement) return;
          event.preventDefault();
          const fields = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>("input, button[type=submit]"),
          );
          const next = fields[fields.indexOf(target) + 1];
          next?.focus();
        }}
      >
        <ErrorNote error={error} />
        <label className="mt-1.5 text-sm text-muted" htmlFor="name">
          Name
        </label>
        <Input id="name" name="name" autoComplete="name" enterKeyHint="next" required autoFocus />
        <label className="mt-1.5 text-sm text-muted" htmlFor="email">
          Email
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          enterKeyHint="next"
          defaultValue={invitedEmail}
          required
        />
        {invitedEmail ? (
          <p className="mt-1 text-xs text-muted">
            Your invitation was sent to this address — keep it to join the group waiting
            for you.
          </p>
        ) : null}
        <label className="mt-1.5 text-sm text-muted" htmlFor="password">
          Password
        </label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          enterKeyHint="next"
          minLength={10}
          required
        />
        <p className="mt-1 text-xs text-muted">At least 10 characters.</p>
        <Button type="submit" variant="primary" disabled={busy} className="mt-4">
          {busy ? "Sending…" : "Continue"}
        </Button>
      </form>

      <p className="mt-3 text-xs text-muted">
        We will email you a link to confirm the address. Your account is created when you
        follow it — that is what proves the address is yours, and it is what connects you
        to any group you have already been invited to.
      </p>

      {methods.google ? <GoogleButton siteUrl={methods.site_url} /> : null}

      <p className="mt-6 text-sm text-muted">
        Already have one?{" "}
        <Link className="text-accent underline underline-offset-2" to="/login">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const methods = useAuthMethods();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: String(form.get("email")) });
    } catch {
      // The server answers "ok" whether or not the account exists, and so does this: an
      // error here would be a way to find out which addresses are registered.
    } finally {
      setBusy(false);
      setSent(true);
    }
  }

  return (
    <AuthShell title="Reset your password">
      {sent ? (
        <div className="text-sm">
          <p className="mb-3">
            If there is an account with that address, a reset link is on its way. It works
            for a day, and using it signs you out everywhere.
          </p>
          {!methods.email_delivery ? (
            <p className="mb-3 rounded-lg border border-rule bg-raised px-3 py-2 text-xs text-muted">
              This install has no mail server configured, so the link was written to the
              server log instead. Whoever runs it can find it there.
            </p>
          ) : null}
          <Link className="text-accent underline underline-offset-2" to="/login">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form className="flex flex-col gap-1.5" onSubmit={onSubmit}>
          <label className="text-sm text-muted" htmlFor="email">
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            enterKeyHint="go"
            required
            autoFocus
          />
          <Button type="submit" variant="primary" disabled={busy} className="mt-4">
            {busy ? "Sending…" : "Send a reset link"}
          </Button>
          <p className="mt-3 text-sm">
            <Link className="text-accent underline underline-offset-2" to="/login">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const { token = "" } = useParams();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  /**
   * Which token this component has already spent.
   *
   * A confirmation token is single-use, so sending it twice reports "already used" — and
   * the person is looking at a working link being told it is dead. Two separate things
   * made the effect run more than once, and the guard has to survive both:
   *
   * - `refresh` is rebuilt whenever the session context re-renders (it closes over the
   *   query client, and `useMutation` objects change identity constantly), so having it
   *   in the dependency array re-fired the effect the moment the first call succeeded and
   *   invalidated `me`.
   * - React StrictMode deliberately double-invokes effects in development.
   *
   * A ref rather than state because it must be set *before* the request goes out, and a
   * state update would not have landed by the time the second invocation checked it.
   */
  const spent = useRef<string | null>(null);

  // Spent on arrival: the person followed a link, which *is* the action. A button here
  // would be a second step for no reason — and following the link from an email often
  // happens in a browser with no session, so this also signs them in.
  useEffect(() => {
    if (!token || spent.current === token) return;
    spent.current = token;

    api
      .post(`/auth/verify/${encodeURIComponent(token)}`)
      .then(async () => {
        await refresh();
        // Straight in, with the banner rendered by the app shell. A "confirmed, taking
        // you in…" interstitial is a screen nobody needs to read.
        navigate("/?confirmed=1", { replace: true });
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : "That link did not work.");
      });
    // `navigate` and `refresh` are deliberately not dependencies — see `spent` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthShell title="Confirming your email">
      {error ? (
        <div className="text-sm">
          <ErrorNote error={error} />
          <p className="mt-3 text-muted">
            If you have already confirmed this address, just sign in. Otherwise sign up
            again to get a fresh link.
          </p>
          <p className="mt-3 flex gap-4">
            <Link className="text-accent underline underline-offset-2" to="/login">
              Sign in
            </Link>
            <Link className="text-accent underline underline-offset-2" to="/register">
              Sign up again
            </Link>
          </p>
        </div>
      ) : (
        <Spinner />
      )}
    </AuthShell>
  );
}

export function ResetPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/auth/reset/${encodeURIComponent(token)}`, {
        password: String(form.get("password")),
      });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not set the password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Set a new password">
      {done ? (
        <p className="text-sm">Password set. Taking you to the sign-in page…</p>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <ErrorNote error={error} />
          <label className="text-sm" htmlFor="password">
            New password
          </label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            autoFocus
          />
          <Button type="submit" variant="primary" disabled={busy} className="mt-2">
            {busy ? "Saving…" : "Set password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
